import { ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { SocialFeedService } from '../../services/social-feed.service';
import { ReportReason } from '../../models/social-feed.model';

/**
 * "Report this post/comment" dialog: a reason, optional details, Submit.
 * The parent just says what's being reported (postId or commentId) and
 * whether the dialog is open; this owns the reason/details/submitting state
 * and calls the API itself, closing on success with a brief confirmation.
 */
@Component({
  selector: 'app-social-report-dialog',
  templateUrl: './social-report-dialog.component.html',
  styleUrls: ['./social-report-dialog.component.scss'],
  standalone: false,
})
export class SocialReportDialogComponent implements OnChanges {
  @Input() open = false;
  @Input() target: { postId: number } | { commentId: number } | null = null;
  @Output() closed = new EventEmitter<void>();

  @ViewChild('cancelButton') cancelButton?: ElementRef<HTMLButtonElement>;

  readonly reasons: { value: ReportReason; label: string }[] = [
    { value: 'spam', label: 'Spam' },
    { value: 'harassment', label: 'Harassment or bullying' },
    { value: 'inappropriate', label: 'Inappropriate content' },
    { value: 'other', label: 'Something else' },
  ];

  reason: ReportReason | null = null;
  details = '';
  submitting = false;
  error = '';
  done = false;

  constructor(private socialFeedService: SocialFeedService, private cdr: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['open'] && this.open) {
      this.reason = null;
      this.details = '';
      this.error = '';
      this.done = false;
      this.submitting = false;
      // Start on the safe choice, so Enter can't submit by accident.
      setTimeout(() => this.cancelButton?.nativeElement.focus());
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.open && !this.submitting) {
      this.close();
    }
  }

  close(): void {
    this.closed.emit();
  }

  submit(): void {
    if (!this.reason || !this.target || this.submitting) {
      return;
    }
    this.submitting = true;
    this.error = '';
    this.socialFeedService.report(this.target, this.reason, this.details.trim()).subscribe({
      next: () => {
        this.submitting = false;
        this.done = true;
        this.cdr.detectChanges();
        setTimeout(() => this.close(), 1400);
      },
      error: (err) => {
        this.error = err?.message || "Couldn't send your report. Please try again.";
        this.submitting = false;
        this.cdr.detectChanges();
      },
    });
  }
}
